import { useDebounce } from "../../../hooks/use-debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import { ServiceTypeRow, ServiceTypeStatus } from "../data/service-type-data";
import {
  ServiceTypeCreateModal,
  ServiceTypeFormValues,
} from "../components/service-type-create-modal";
import { ServiceTypeStatusConfirmModal } from "../components/service-type-status-confirm-modal";
import ServiceTypeDetailModal from "../components/service-type-detail-modal";
import { ALL_BRANCHES_VALUE, useCatalogBranchOptions } from "../../../hooks/use-catalog-branch-options";
import {
  ChangeServiceTypeStatusPayload,
  CreateServiceTypePayload,
  ServiceType,
  ServiceTypeListFilters,
  ServiceTypeService,
  ServiceTypeStatusCode,
} from "../services/service-type-service";
import ServiceTypeTable from "../components/service-type-table";
// Tạm ẩn copy từ chi nhánh (danh mục dùng chung) — mở lại cùng bộ lọc chi nhánh
// import CopyFromBranchModal from "../../common/copy-from-branch-modal";
// import { ApiConfig, API_SERVICE_TYPE_MAPS_ROUTER } from "../../../services/api-config";

type ServiceTypeTabKey = "active" | "inactive";
type PendingStatusAction = { row: ServiceTypeRow; targetStatus: ServiceTypeStatus; };

const mapStatusCodeToLabel = (code: ServiceTypeStatusCode): ServiceTypeStatus => {
  switch (code) {
    case ServiceTypeStatusCode.ACTIVE: return "active";
    case ServiceTypeStatusCode.DELETED: return "deleted";
    default: return "inactive";
  }
};

export const ServiceTypeIndexPage: React.FC = () => {
  const [rows, setRows] = useState<ServiceTypeRow[]>([]);
  const [activeTab, setActiveTab] = useState<ServiceTypeTabKey>("active");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingStatusAction | null>(null);
  const [detailRow, setDetailRow] = useState<ServiceTypeRow | null>(null);
  const [detailData, setDetailData] = useState<ServiceType | undefined>();
  const [editRow, setEditRow] = useState<ServiceTypeRow | null>(null);
  
  // Pagination and Search State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [keysearch, setKeysearch] = useState('');
  const [activeRecords, setActiveRecords] = useState(0);
  const [inactiveRecords, setInactiveRecords] = useState(0);
  const debouncedSearch = useDebounce(keysearch, 700);

  // Stores từ API
  // Có thêm lựa chọn "Dùng chung" (branch_id = 0)
  const { options: storeOptions, branchLabel } = useCatalogBranchOptions();
  // Tạm cố định "Dùng chung" (branch_id = 0) — danh mục đang vận hành dùng chung cho mọi chi nhánh.
  // Mở lại bộ lọc: đổi về useState<number | null>(null) + bỏ comment effect "Set default store" và khối "Branch Filter".
  const [selectedbranchId] = useState<number | null>(ALL_BRANCHES_VALUE);
  // const [openCopy, setOpenCopy] = useState(false);

  // Service API
  const { fetch: fetchDetail, loading: loadingDetail } = ServiceTypeService.detail();
  const { fetch: fetchServices, loading } = ServiceTypeService.list();
  const { fetch: createService, loading: creating } = ServiceTypeService.create();
  const { fetch: update, loading: updating } = ServiceTypeService.update();
  const { fetch: changeStatus, loading: changingStatus } = ServiceTypeService.changeStatus();
  
  const listRef = useRef(fetchServices);
  useEffect(() => {
    listRef.current = fetchServices;
  }, [fetchServices]);

  // Set default store khi có options — tạm tắt, đang cố định "Dùng chung"
  // useEffect(() => {
  //   if (selectedbranchId == null && storeOptions.length > 0) {
  //     setSelectedbranchId(Number(storeOptions[0].value));
  //   }
  // }, [storeOptions, selectedbranchId]);

  // Reset page khi đổi tab hoặc đổi chi nhánh
  useEffect(() => {
    setPage(1);
  }, [activeTab, selectedbranchId]);

  const findStoreLabel = (branchId: number): string => {
    return branchLabel(branchId);
  };

  const mapDtoToRow = (dto: ServiceType): ServiceTypeRow => {
    const statusCode = Number(dto.status);
    return {
      id: dto.id,
      serviceTypeId: dto.id,
      name: dto.name,
      code: dto.code,
      storeName: findStoreLabel(dto.branch_id),
      branchId: dto.branch_id,
      status: mapStatusCodeToLabel(statusCode as ServiceTypeStatusCode),
      statusCode,
      description: dto.description,
      createdAt: dto.created_at,
      updatedAt: dto.updated_at,
    };
  };

  const loadServiceTypes = useCallback(async () => {
    if (selectedbranchId == null) return;

    try {
      const status = activeTab === 'active' ? 1 : 0;
      const params: ServiceTypeListFilters = { 
        status, 
        branch_id: selectedbranchId,
        page: page,
        limit: limit,
        key_search: debouncedSearch,
      };
      
      const response = await listRef.current(params);

      if (!response) {
        showToast({ message: "Không thể tải danh sách dịch vụ", type: "error" });
        return;
      }

      if (isSuccess(response)) {
        const list = response.data?.list ?? [];
        setRows(list.map(mapDtoToRow));
        const total = Number(response.data?.total_record ?? 0);
        if (activeTab === 'active') {
          setActiveRecords(total);
        } else {
          setInactiveRecords(total);
        }
      } else {
        showToast({ message: response.message ?? "Không thể tải danh sách dịch vụ", type: "error" });
      }
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : "Không thể tải danh sách dịch vụ",
        type: "error",
      });
    }
  }, [selectedbranchId, page, limit, activeTab, debouncedSearch]);

  useEffect(() => {
    void loadServiceTypes();
  }, [loadServiceTypes]);

  const handleCreateServiceType = async (values: ServiceTypeFormValues): Promise<boolean> => {
    const payload: CreateServiceTypePayload = {
      branch_id: Number(values.branchId),
      code: values.code.trim().toUpperCase(),
      name: values.name.trim(),
      status: values.isActive ? ServiceTypeStatusCode.ACTIVE : ServiceTypeStatusCode.INACTIVE,
    };

    try {
      const response = await createService(payload);
      if (!response) {
        showToast({ message: "Không thể tạo loại dịch vụ", type: "error" });
        return false;
      }
      if (isSuccess(response)) {
        showToast({ message: "Tạo loại dịch vụ thành công", type: "success" });
        await loadServiceTypes();
        setActiveTab(values.isActive ? "active" : "inactive");
        return true;
      }
      showToast({ message: response?.message ?? "Không thể tạo loại dịch vụ", type: "error" });
      return false;
    } catch (error) {
      showToast({ message: error instanceof Error ? error.message : "Không thể tạo loại dịch vụ", type: "error" });
      return false;
    }
  };

  // Quyền tạo / sửa / bật-tắt do BE kiểm tra (product service: @Privileges trên ServiceTypeController) —
  // web không tự chặn nữa, thiếu quyền thì hiện thông báo lỗi BE trả về.
  const handleRequestStatusChange = (row: ServiceTypeRow, targetStatus: ServiceTypeStatus) => {
    setPendingAction({ row, targetStatus });
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingAction) return;

    const payload: ChangeServiceTypeStatusPayload = {
      status: pendingAction.targetStatus === "active" ? ServiceTypeStatusCode.ACTIVE : ServiceTypeStatusCode.INACTIVE,
    };

    try {
      const response = await changeStatus(pendingAction.row.id, payload);
      if (!response) {
        showToast({ message: "Không thể cập nhật trạng thái", type: "error" });
        return;
      }
      if (isSuccess(response)) {
        showToast({
          message: pendingAction.targetStatus === "active" ? "Đã kích hoạt dịch vụ" : "Đã ngưng hoạt động dịch vụ",
          type: "success",
        });
        await loadServiceTypes();
      } else {
        showToast({ message: response?.message ?? "Không thể cập nhật trạng thái", type: "error" });
      }
    } catch (error) {
      showToast({ message: error instanceof Error ? error.message : "Không thể cập nhật trạng thái", type: "error" });
    } finally {
      setPendingAction(null);
    }
  };

  const handleViewDetail = async (row: ServiceTypeRow) => {
    setDetailRow(row);
    setDetailData(undefined);

    try {
      const response = await fetchDetail(row.id);
      if (!response) {
        showToast({ message: "Không thể tải chi tiết dịch vụ", type: "error" });
        setDetailRow(null);
        return;
      }

      if (isSuccess(response)) {
        setDetailData(response.data);
      } else {
        showToast({ message: response.message ?? "Không thể tải chi tiết dịch vụ", type: "error" });
        setDetailRow(null);
      }
    } catch (error) {
      showToast({ message: error instanceof Error ? error.message : "Không thể tải chi tiết dịch vụ", type: "error" });
      setDetailRow(null);
    }
  };

  const handleCloseDetailModal = () => { setDetailRow(null); setDetailData(undefined); };
  
  const onEditSubmit = async (values: ServiceTypeFormValues): Promise<boolean> => {
    if (!editRow) return false;
  
    const payload = {
      code: values.code.trim().toUpperCase(),
      name: values.name.trim(),
      status: values.isActive ? ServiceTypeStatusCode.ACTIVE : ServiceTypeStatusCode.INACTIVE,
    };
  
    try {
      const response = await update(editRow.id, payload);
      if (!response) {
        showToast({ message: "Không thể cập nhật loại dịch vụ", type: "error" });
        return false;
      }
      if (isSuccess(response)) {
        showToast({ message: "Cập nhật loại dịch vụ thành công", type: "success" });
        setEditRow(null);
        await loadServiceTypes();
        return true;
      }
      showToast({ message: response?.message ?? "Không thể cập nhật loại dịch vụ", type: "error" });
      return false;
    } catch (error) {
      showToast({ message: error instanceof Error ? error.message : "Không thể cập nhật loại dịch vụ", type: "error" });
      return false;
    }
  };

  const handleEdit = (row: ServiceTypeRow) => {
    setEditRow(row);
  };

  return (
    <div className="space-y-6">
      
      {/* --- HEADER --- */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-green-600 pl-3">
          Sản phẩm dịch vụ
        </h1>
        <div className="flex items-center gap-2">
          {/* Tạm ẩn: copy từ chi nhánh không dùng khi danh mục dùng chung — mở lại cùng bộ lọc chi nhánh
          <button
            type="button"
            onClick={() => {
              if (selectedbranchId == null) {
                showToast({ message: "Vui lòng chọn chi nhánh trước", type: "error" });
                return;
              }
              if (selectedbranchId === ALL_BRANCHES_VALUE) {
                showToast({ message: "Danh mục dùng chung đã áp dụng cho mọi chi nhánh — chọn một chi nhánh cụ thể để copy", type: "error" });
                return;
              }
              setOpenCopy(true);
            }}
            className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-blue-600 shadow-sm transition hover:border-blue-300 hover:bg-blue-100"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span>Copy từ chi nhánh khác</span>
          </button> */}
          <button
            type="button"
            onClick={() => setIsCreateOpen(true)}
            className="inline-flex items-center gap-3 rounded-full border border-green-200 bg-green-50 px-8 py-3 text-base font-semibold uppercase tracking-wide text-green-600 shadow-sm transition hover:border-green-300 hover:bg-green-100"
          >
            <span className="text-2xl leading-none text-green-500">+</span>
            <span>Thêm mới</span>
          </button>
        </div>
      </div>

      {/* --- MAIN CARD --- */}
      <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
        
        {/* --- TOOLBAR (TABS & FILTER) --- */}
        <div className="flex flex-col gap-3 rounded-2xl bg-slate-100 px-6 py-4 md:flex-row md:items-center md:justify-between">
          
          {/* Tabs */}
          <div className="flex items-center gap-8">
            <button
              onClick={() => setActiveTab("active")}
              className={`relative flex items-center gap-2 pb-2 text-sm font-semibold uppercase tracking-wide transition ${
                activeTab === "active"
                  ? "text-green-600"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              <span>Đang hoạt động</span>
              <span
                className={`${
                  activeTab === "active"
                    ? "bg-green-100 text-green-600"
                    : "bg-slate-200 text-slate-500"
                } rounded-full px-2.5 py-0.5 text-xs font-semibold`}
              >
                {activeRecords}
              </span>
              {activeTab === "active" ? (
                <span className="pointer-events-none absolute inset-x-0 -bottom-1 h-1 rounded-full bg-green-500" />
              ) : null}
            </button>
            <button
              onClick={() => setActiveTab("inactive")}
              className={`relative flex items-center gap-2 pb-2 text-sm font-semibold uppercase tracking-wide transition ${
                activeTab === "inactive"
                  ? "text-green-600"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              <span>Tạm ngưng</span>
              <span
                className={`${
                  activeTab === "inactive"
                    ? "bg-green-100 text-green-600"
                    : "bg-slate-200 text-slate-500"
                } rounded-full px-2.5 py-0.5 text-xs font-semibold`}
              >
                {inactiveRecords}
              </span>
              {activeTab === "inactive" ? (
                <span className="pointer-events-none absolute inset-x-0 -bottom-1 h-1 rounded-full bg-green-500" />
              ) : null}
            </button>
          </div>

          {/* Branch Filter — tạm ẩn, đang cố định "Dùng chung" (branch_id = 0)
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-slate-600">Chi nhánh:</label>
            <select
              value={selectedbranchId ?? ""}
              onChange={(e) => setSelectedbranchId(Number(e.target.value))}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              {loadingStores && <option>Đang tải...</option>}
              {!loadingStores && storeOptions.length === 0 && <option>Không có chi nhánh</option>}
              {storeOptions.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div> */}
        </div>

        {/* --- CONTENT AREA --- */}
        <div className="mt-6">
          <ServiceTypeTable
            rows={rows}
            onEdit={handleEdit}   
            onDeactivate={activeTab === 'active' ? (row) => handleRequestStatusChange(row, "inactive") : undefined}
            onActivate={activeTab === 'inactive' ? (row) => handleRequestStatusChange(row, "active") : undefined}
            onViewDetail={handleViewDetail}
            isLoading={loading}
            startIndex={(page - 1) * limit + 1}
            page={page}
            pageSize={limit}
            search={keysearch}
            setPage={setPage}
            setPageSize={setLimit}
            setSearch={setKeysearch}
            totalRecords={activeTab === 'active' ? activeRecords : inactiveRecords}
          />
        </div>
      </div>

      {/* --- MODALS --- */}
      {isCreateOpen && selectedbranchId != null && (
        <ServiceTypeCreateModal
          storeOptions={[
            ...storeOptions.filter(o => Number(o.value) === selectedbranchId),
            ...storeOptions.filter(o => Number(o.value) !== selectedbranchId),
          ]}
          onClose={() => setIsCreateOpen(false)}
          onSubmit={handleCreateServiceType}
          isSubmitting={creating}
          initialValues={{
            code: "",
            name: "",
            branchId: selectedbranchId ?? storeOptions[0]?.value ?? "",
            description: "",
            isActive: true,
          }}
        />
      )}
      
      {editRow && (
        <ServiceTypeCreateModal
          storeOptions={[
            ...storeOptions.filter(o => Number(o.value) === selectedbranchId),
            ...storeOptions.filter(o => Number(o.value) !== selectedbranchId),
          ]}
          onClose={() => setEditRow(null)}
          onSubmit={onEditSubmit}
          isSubmitting={updating}
          initialValues={{
            code: editRow.code,
            name: editRow.name,
            description: editRow.description ?? "",
            isActive: editRow.statusCode === ServiceTypeStatusCode.ACTIVE,
          } as ServiceTypeFormValues}
        />
      )}

      {pendingAction && (
        <ServiceTypeStatusConfirmModal
          mode={pendingAction.targetStatus === "inactive" ? "deactivate" : "activate"}
          serviceTypeName={pendingAction.row.name}
          onCancel={() => setPendingAction(null)}
          onConfirm={handleConfirmStatusChange}
          isProcessing={changingStatus}
        />
      )}

      {detailRow && (
        <ServiceTypeDetailModal
          rowName={detailRow.name}
          data={detailData}
          loading={loadingDetail}
          onClose={handleCloseDetailModal}
        />
      )}

      {/* Tạm ẩn cùng nút "Copy từ chi nhánh khác"
      <CopyFromBranchModal
        isOpen={openCopy}
        onClose={() => setOpenCopy(false)}
        toBranchId={selectedbranchId ?? undefined}
        apiPath={API_SERVICE_TYPE_MAPS_ROUTER.API_COPY_ALL_FROM_BRANCH}
        svcId={ApiConfig.PROJECT_ID.PRODUCT_SERVICE}
        title="Copy Sản phẩm + Nhóm dịch vụ từ chi nhánh khác"
        description="Copy cả sản phẩm dịch vụ và nhóm dịch vụ (kèm giá mapping) từ chi nhánh nguồn sang chi nhánh đang xem. Hệ thống sẽ copy theo thứ tự đúng: sản phẩm dịch vụ trước, nhóm dịch vụ sau."
        onSuccess={() => void loadServiceTypes()}
      /> */}
    </div>
  );
};

export default ServiceTypeIndexPage;